import { PageWrapper } from "@/components";
import { PermissionRights } from "@/components/PermissionEditor/components/PermissionRights";
import { UserInfoCard } from "@/components/UserInfo";
import { ProjectRolesViewer } from "@/containers/Profile/components";
import { ConditionalAccessViewer } from "@/containers/Profile/components/ConditionalAccessViewer";
import { getLookupValues } from "@/containers/Profile/helpers";
import { FormType } from "@/enums";
import Rights from "@/enums/Rights";
import { mergeUserContexts, routes } from "@/helpers";
import { useUserContext } from "@/providers";
import {
  useGetDefaultRolesForUserQuery,
  useGetFormLookupsQuery,
  useGetUserQuery,
} from "@/services";
import { faShieldKeyhole } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Space, Tabs } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

export const Profile = () => {
  // app state
  const { userToken, hasRight } = useUserContext();

  const { id } = useParams();

  const intl = useIntl();

  const [formLookupMap, setFormLookupMap] = useState({});
  const [currentTab, setCurrentTab] = useState();

  const { data: formLookupState } = useGetFormLookupsQuery();
  const { data: defaultedRoles, isLoading: defaultsLoading } =
    useGetDefaultRolesForUserQuery(id);

  const [functionalAreas, setFunctionalAreas] = useState([]);
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    if (!formLookupState) return;

    setFunctionalAreas(formLookupState[FormType.Intake].functionalArea);
    setRegions(formLookupState[FormType.Intake].region);
  }, [formLookupState]);

  const [userAccess, setUserAccess] = useState({
    canImpersonate: false,
    canViewPermissions: false,
    canWritePermissions: false,
  });

  useEffect(() => {
    setUserAccess({
      canImpersonate: hasRight([Rights.impersonateUsers]),
      canViewPermissions: hasRight([
        Rights.readPermissions,
        Rights.writePermissions,
      ]),
      canWritePermissions: hasRight([Rights.writePermissions]),
    });
  }, [hasRight]);

  useEffect(() => {
    if (!formLookupState) return;

    const toUpdate = Object.values(formLookupState).reduce(
      (previousValue, currentValue) => {
        if (currentValue.projectType) {
          previousValue = {
            ...previousValue,
            projectType: {
              ...previousValue.projectType,
              ...getLookupValues(currentValue.projectType, intl),
            },
          };
        }
        if (currentValue.productCategory) {
          previousValue = {
            ...previousValue,
            productCategory: {
              ...previousValue.productCategory,
              ...getLookupValues(currentValue.productCategory, intl),
            },
          };
        }
        return previousValue;
      },
      {}
    );
    setFormLookupMap(toUpdate);
  }, [formLookupState, intl]);

  // local state
  const [state, setState] = useState({
    loading: true,
    userProfile: userToken?.user,
    rights: userToken?.rights,
    businessUnit: userToken?.businessUnit,
    region: userToken?.region,
    location: userToken?.location,
    companies: userToken?.companies,
    isOwnProfile: true,
  });

  const { data: user, isError, refetch: refetchUser } = useGetUserQuery(id);

  useEffect(() => {
    if (!isError) return;

    toast.error(intl.formatMessage({ id: "profile.loadingUserErrorMessage" }));
  }, [intl, isError]);

  useEffect(() => {
    if (!user?.id || user.id.toLowerCase() !== id?.toLowerCase()) return;

    const userContext = mergeUserContexts(user);
    const metadata = userContext?.metadata;

    setState((prevState) => ({
      ...prevState,
      loading: false,
      userProfile: user,
      rights: userContext?.roles || [],
      businessUnit: metadata?.businessUnit || "",
      region: metadata?.region || "",
      location: metadata?.location || "",
      companies: metadata?.companies || "",
      conditionalAccess: metadata
        ? Object.keys(metadata)
            .filter((propertyName) =>
              propertyName.startsWith("ConditionalAccess_")
            )
            .map((propertyName) => ({
              ...JSON.parse(metadata[propertyName]),
              adGroupName: propertyName.replace("ConditionalAccess_", ""),
            }))
        : [],
      isOwnProfile: userToken?.user?.id === user?.id,
    }));
  }, [id, user, userToken?.user?.id]);

  const tabs = [
    {
      key: "permissions",
      label: intl.formatMessage({ id: "profile.permissions" }),
      children: (
        <Card
          title={intl.formatMessage({ id: "permissions.rightsCardTitle" })}
          style={{ height: "auto", marginBottom: "12px" }}
          loading={state.loading}
        >
          <PermissionRights
            setSelectedRights={() => {}}
            selectedRights={state?.rights}
            canEditRights={false}
          />
        </Card>
      ),
    },
    {
      key: "conditionalAccess",
      label: intl.formatMessage({ id: "profile.conditionalAccess" }),
      children: (
        <Card
          title={
            <Space>
              <FontAwesomeIcon
                icon={faShieldKeyhole}
                style={{ fontSize: "18px" }}
              />
              {intl.formatMessage({
                id: "profile.conditionalAccess",
              })}
            </Space>
          }
          style={{ height: "auto", marginBottom: "12px" }}
          loading={state.loading}
        >
          <ConditionalAccessViewer
            conditionalAccess={state?.conditionalAccess}
            formLookup={formLookupMap}
          />
        </Card>
      ),
    },
    {
      key: "projectRoles",
      label: intl.formatMessage({ id: "profile.projectRoles" }),
      children: (
        <Card
          title={intl.formatMessage({ id: "profile.projectRoles" })}
          style={{ height: "auto", marginBottom: "12px" }}
          loading={defaultsLoading}
        >
          <ProjectRolesViewer
            regions={regions}
            functionalAreas={functionalAreas}
            defaultRoles={defaultedRoles}
          />
        </Card>
      ),
    },
  ];

  const handleTabChange = (e) => {
    setCurrentTab(e.key);
  };

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "profile.title" })}
      backButtonRoute={routes.admin.users.list}
    >
      <UserInfoCard user={user} refetchUser={refetchUser} />
      {userAccess.canViewPermissions && (
        <Tabs
          className="pt-2"
          data-test-id="profile-tabs"
          centered
          size="large"
          activeKey={currentTab}
          defaultActiveKey={"permissions"}
          items={tabs}
          onChange={handleTabChange}
        />
      )}
    </PageWrapper>
  );
};
